import { useState, useRef, useEffect } from 'react'
import { Send, Sparkles, X, Check, ChevronDown } from 'lucide-react'

interface Message {
  role: 'assistant' | 'user' | 'system'
  content: string
  suggestedValue?: string
  applied?: boolean
}

interface RevisionContext {
  previousConcept: string
  brandFeedback: string
}

interface AiCreativeAssistantChatProps {
  brandName: string
  revisionContext?: RevisionContext
  onApplyConcept: (value: string) => void
  onClose: () => void
}

function getMockOpener(brandName: string, revisionContext?: RevisionContext): string {
  if (revisionContext) {
    const feedback = revisionContext.brandFeedback || 'they want some adjustments'
    return `Hey! The brand came back with feedback: "${feedback}"\n\nHere's what you submitted:\n\n"${revisionContext.previousConcept}"\n\nWant me to come up with a new concept that incorporates this feedback? Or do you have any ideas you want to add?`
  }
  return `Let's figure out your creative concept for ${brandName}. No need to overthink it -- just talk me through what you're thinking.\n\nWhat kind of content do you naturally make? Training clips, day-in-the-life, routines? And where does ${brandName} fit into your world?`
}

function getMockReply(userMessage: string, brandName: string): string {
  const lower = userMessage.toLowerCase()

  if (
    lower.includes('routine') ||
    lower.includes('training') ||
    lower.includes('workout') ||
    lower.includes('gym')
  ) {
    return `Love that energy. Here is a concept draft you can use:\n\n"For this collab I am planning a training routine video on TikTok. I will film my full workout prep and bring in ${brandName} naturally at the moment I reach for it -- no forced product demo, just how I actually use it. I will close with a quick line about what I like about it and drop a link for anyone who wants to try it."\n\nWant me to tweak the tone or make it more specific to your sport?`
  }
  if (
    lower.includes('day') ||
    lower.includes('vlog') ||
    lower.includes('behind') ||
    lower.includes('life')
  ) {
    return `Day-in-the-life is a great fit. Here is a draft:\n\n"I am shooting a Day in My Life as an Athlete TikTok and ${brandName} fits right into my morning prep. I will show my actual routine -- no script -- and work in the brand where it genuinely shows up. Keeping it real and unfiltered, which is what my audience responds to."\n\nHappy with that? Or want to shift the angle?`
  }
  if (
    lower.includes('travel') ||
    lower.includes('game') ||
    lower.includes('competition') ||
    lower.includes('tournament')
  ) {
    return `Game day content is super relatable. Here is a concept:\n\n"I am going to create a game day TikTok and bring ${brandName} in as part of my prep ritual. Athletes travel constantly and my followers get that -- showing how I pack and what I rely on resonates way more than a straight ad. I will keep it honest and quick."\n\nWant to dial in any specific details?`
  }
  if (
    lower.includes('tweak') ||
    lower.includes('change') ||
    lower.includes('different') ||
    lower.includes('adjust')
  ) {
    return `Got it -- here is an updated take:\n\n"This TikTok is going to feel like a real behind-the-scenes look at my week. ${brandName} comes up naturally because it is part of how I actually operate -- I am not going to make it the star of the video, just let it show up authentically. I will be honest about why I use it and give my audience a way to check it out."\n\nCloser to what you are thinking?`
  }

  return `Here is a concept based on what you described:\n\n"For this ${brandName} collab I am creating a TikTok that shows how the brand fits into my actual life as an athlete. The vibe is authentic and unscripted -- my audience has seen enough ads, so I am going to show them how I really use it and let that do the work. I will keep the CTA low-key: mention it, show it, drop the link."\n\nThat feel right? Tell me if you want a different tone or format.`
}

function extractSuggestion(content: string): string | null {
  const match = content.match(/"([^"]{40,})"/)
  return match ? match[1] : null
}

const AiCreativeAssistantChat = ({
  brandName,
  revisionContext,
  onApplyConcept,
  onClose,
}: AiCreativeAssistantChatProps) => {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: getMockOpener(brandName, revisionContext) },
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastAiMessageRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const inputRef = useRef<HTMLDivElement>(null)

  // Slide in on mount, lock page scroll
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = ''
    }
  }, [])

  // Scroll behavior:
  // - User message or typing indicator → scroll to bottom (show what was just sent)
  // - AI response → scroll to TOP of that message so athlete reads it naturally top-to-bottom
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const lastMsg = messages[messages.length - 1]

    if (isLoading || !lastMsg || lastMsg.role === 'user' || lastMsg.role === 'system') {
      container.scrollTop = container.scrollHeight
    } else if (lastMsg.role === 'assistant' && lastAiMessageRef.current) {
      const containerRect = container.getBoundingClientRect()
      const msgRect = lastAiMessageRef.current.getBoundingClientRect()
      container.scrollTop += msgRect.top - containerRect.top - 20
    }
  }, [messages, isLoading])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 280)
  }

  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }])
    setInput('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    setIsLoading(true)

    await new Promise((r) => setTimeout(r, 1200))

    const replyContent = getMockReply(trimmed, brandName)
    const suggestion = extractSuggestion(replyContent)

    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: replyContent, suggestedValue: suggestion || undefined },
    ])
    setIsLoading(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const renderContent = (content: string) => {
    return content.split('\n').map((line, i, arr) => {
      const parts = line.split(/("[^"]{20,}")/)
      return (
        <span key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('"') && part.endsWith('"') && part.length > 22) {
              return (
                <span
                  key={j}
                  className="block mt-2 mb-1 text-white/90 bg-white/8 rounded-xl px-3 py-2.5 text-sm italic border border-white/10"
                >
                  {part}
                </span>
              )
            }
            return <span key={j}>{part}</span>
          })}
          {i < arr.length - 1 && <br />}
        </span>
      )
    })
  }

  return (
    // Backdrop
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        className={`relative flex flex-col bg-[#0f1623] rounded-t-3xl transition-transform duration-300 ease-out ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ height: '88dvh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C0FD72]" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold leading-tight">AI Assistant</p>
              <p className="text-gray-500 text-xs">Helping you draft for {brandName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        {/* Messages — fills all remaining space, single scroll */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-4 py-5 space-y-5 overscroll-contain"
        >
          {(() => {
            const lastAiIdx = messages.reduce(
              (last, m, idx) => (m.role === 'assistant' ? idx : last),
              -1,
            )
            return messages.map((msg, i) =>
              msg.role === 'system' ? (
                <div key={i} className="flex justify-center py-1">
                  <div className="flex items-center gap-1.5 bg-[#C0FD72]/10 border border-[#C0FD72]/20 rounded-full px-3 py-1.5">
                    <Check className="w-3 h-3 text-[#C0FD72]" />
                    <span className="text-[#C0FD72] text-xs font-medium">{msg.content}</span>
                  </div>
                </div>
              ) : (
                <div
                  key={i}
                  ref={msg.role === 'assistant' && i === lastAiIdx ? lastAiMessageRef : null}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/25 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#C0FD72]" />
                    </div>
                  )}
                  <div className={`max-w-[82%] ${msg.role === 'assistant' ? 'space-y-3' : ''}`}>
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#C0FD72] text-black rounded-tr-sm font-medium'
                          : 'bg-white/8 text-gray-200 rounded-tl-sm border border-white/8'
                      }`}
                    >
                      {msg.role === 'assistant' ? renderContent(msg.content) : msg.content}
                    </div>
                    {msg.suggestedValue && (
                      <button
                        onClick={() => {
                          onApplyConcept(msg.suggestedValue!)
                          setMessages((prev) =>
                            prev
                              .map((m, idx) => (idx === i ? { ...m, applied: true } : m))
                              .concat({
                                role: 'system',
                                content: 'Applied. Keep refining or close to review it.',
                              }),
                          )
                        }}
                        disabled={msg.applied}
                        className={`w-full text-sm font-semibold py-3 px-4 rounded-2xl transition-colors text-left flex items-center gap-2 ${
                          msg.applied
                            ? 'bg-white/10 text-gray-400 cursor-default'
                            : 'bg-[#C0FD72] text-black hover:bg-[#b0ef62]'
                        }`}
                      >
                        {msg.applied ? (
                          <>
                            <Check className="w-4 h-4 shrink-0" />
                            Applied to your concept
                          </>
                        ) : (
                          'Use this concept \u2192'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ),
            )
          })()}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-7 h-7 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/25 flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#C0FD72]" />
              </div>
              <div className="bg-white/8 border border-white/8 rounded-2xl rounded-tl-sm px-5 py-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:160ms]" />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:320ms]" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input — pinned to bottom of sheet, always above keyboard */}
        <div
          ref={inputRef}
          className="shrink-0 px-4 pb-8 pt-3 border-t border-white/8 bg-[#0f1623]"
        >
          <div className="flex items-end gap-3 bg-white/8 border border-white/12 rounded-2xl px-4 py-3">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                autoGrow(e.target)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Message your assistant..."
              className="flex-1 bg-transparent text-white placeholder:text-gray-600 text-base resize-none outline-hidden leading-relaxed min-h-[24px]"
              rows={1}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="w-10 h-10 bg-[#C0FD72] hover:bg-[#b0ef62] disabled:opacity-30 text-black rounded-full flex items-center justify-center shrink-0 transition-all disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AiCreativeAssistantChat
